import { Button, CloseButton, Dialog, DialogBody, DialogContent, DialogFooter, Drawer, Field, Flex, InputGroup, Portal, Span } from '@chakra-ui/react';
import { convertUTCToTimezoneDate, DatePicker, formatDateUTC, FormInput, Loading, toaster, useDrawer } from '@shared';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAddDiscountCode, useDeleteDiscountCode, useGetSingleDiscountCode, useUpdateDiscountCode } from '../../api/hooks/useDiscountCodeSuperadmin';
import { DateTime } from 'luxon';

type DiscountCodeForm = {
	discountPercentage: number;
	name: string;
	code: string;
	usageLimit: number | null;
	timeStart: Date | string | null;
	timeEnd: Date | string | null;
};

const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function DiscountCodeDrawer({ discountCodeID }: { discountCodeID?: number }) {
	const { t } = useTranslation();
	const { mutate: updateDiscountCode, isPending: isUpdatingDiscountCode } = useUpdateDiscountCode();
	const { mutate: addDiscountCode, isPending: isAddingDiscountCode } = useAddDiscountCode();

	const { closeDrawer } = useDrawer();
	const [isRemoveOpen, setIsRemoveOpen] = useState<boolean>(false);
	const queryClient = useQueryClient();

	const { data: discountCode, isFetching: isFetchingDiscountCode } = useGetSingleDiscountCode(discountCodeID as number, {
		isEnabled: !!discountCodeID,
	});

	const methods = useForm<DiscountCodeForm>();

	const {
		handleSubmit,
		reset,
		setValue,
		control,
		formState: { dirtyFields, isDirty },
	} = methods;

	useEffect(() => {
		if (discountCode) {
			reset({
				...discountCode,
			});
		}
	}, [discountCode, reset]);

	function saveDiscountCode(values: DiscountCodeForm): void {
		if (discountCodeID) {
			patchDiscountCode(values);
		} else {
			const payload: AddDiscountCodeRequest = {
				code: values.code,
				name: values.name,
				discountPercentage: values.discountPercentage,
				usageLimit: values.usageLimit,
				timeStart: values.timeStart as string,
				timeEnd: values.timeEnd as string,
			};

			addDiscountCode(payload, {
				onSuccess: async () => {
					toaster.create({
						title: t('general.saved'),
						type: 'success',
					});
					queryClient.refetchQueries({ queryKey: ['discountCodes'] });
					queryClient.refetchQueries({ queryKey: ['discountCode'] });

					closeDrawer('discountCodeDrawer');
				},
			});
		}
	}

	function patchDiscountCode(values: DiscountCodeForm): void {
		if (!discountCodeID) {
			return;
		}

		const payload: UpdateDiscountCodeRequest = {
			discountCodeID: discountCodeID,
		};

		(Object.keys(dirtyFields) as Array<keyof DiscountCodeForm>).forEach(key => {
			const value = values[key];

			if (value !== undefined) {
				if (key === 'timeStart' || key === 'timeEnd') {
					payload[key] = value as string;
				} else if (key === 'code' || key === 'name') {
					payload[key] = value as string;
				} else if (key === 'discountPercentage') {
					payload[key] = value as number;
				} else if (key === 'usageLimit') {
					payload[key] = value as number | null;
				}
			}
		});

		updateDiscountCode(payload, {
			onSuccess: async () => {
				toaster.create({
					title: t('general.saved'),
					type: 'success',
				});
				queryClient.refetchQueries({ queryKey: ['discountCodes'] });
				queryClient.refetchQueries({ queryKey: ['discountCode'] });

				closeDrawer('discountCodeDrawer');
			},
		});
	}

	const code = useWatch({ control, name: 'code' });
	const timeStart = useWatch({ control, name: 'timeStart' });
	const timeEnd = useWatch({ control, name: 'timeEnd' });

	return isFetchingDiscountCode ? (
		<Loading />
	) : (
		<FormProvider {...methods}>
			<form style={{ height: '100%', display: 'flex', flexDirection: 'column' }} onSubmit={handleSubmit(saveDiscountCode)}>
				<Drawer.Header>
					<Drawer.Title marginRight="20px" wordBreak={'break-word'}>
						{discountCodeID ? code : t('general.add_discount_code')}
					</Drawer.Title>
				</Drawer.Header>
				<Drawer.Body flex={1}>
					<Flex flexDirection={'column'} gap={4} marginBottom={3} width={'100%'}>
						<FormInput isRequired label={t('general.name')} name="name" maxLength={100} />
						<FormInput isRequired label={t('general.code')} name="code" maxLength={20} />
						<Flex gap={2}>
							<FormInput
								fieldRootProps={{ width: '50%' }}
								helperText={t('general.leave_empty_for_no_limit')}
								type="number"
								label={t('general.usage_limit')}
								name="usageLimit"
							/>
							<FormInput
								fieldRootProps={{ width: '50%' }}
								type="number"
								isRequired
								label={t('general.discount_percentage')}
								name="discountPercentage"
								max={100}
								min={0}
							/>
						</Flex>

						<Flex flexDirection={'column'}>
							<Field.Root width={'auto'} marginBottom={1}>
								<Field.Label>{t('general.time_start')}</Field.Label>
							</Field.Root>
							<Flex marginBottom={1} alignItems={'center'} gap={2}>
								<InputGroup className="discountCodeTimeStartDatePicker" data-cy="discountCodeTimeStartDatePicker">
									<DatePicker
										placeholderText={t('general.click_to_set_date_and_time')}
										timeFormat="HH:mm"
										showTimeSelect
										dateFormat="dd.MM.yyyy - HH:mm"
										selected={timeStart ? convertUTCToTimezoneDate(timeStart as string) : null}
										onChange={(date: Date | null) => {
											setValue('timeStart', date instanceof Date ? formatDateUTC(date) : null, {
												shouldDirty: true,
											});
										}}
									/>
								</InputGroup>
							</Flex>
							<Span fontSize={'12px'} color="rgb(82, 82, 91)">
								(UTC{DateTime.now().setZone(TIMEZONE).toFormat('ZZ')}) {TIMEZONE}
							</Span>
						</Flex>
						<Flex flexDirection={'column'}>
							<Field.Root width={'auto'} marginBottom={1}>
								<Field.Label>{t('general.time_end')}</Field.Label>
							</Field.Root>
							<Flex marginBottom={1} alignItems={'center'} gap={2}>
								<InputGroup className="discountCodeTimeEndDatePicker" data-cy="discountCodeTimeEndDatePicker">
									<DatePicker
										popperPlacement='top'
										placeholderText={t('general.click_to_set_date_and_time')}
										timeFormat="HH:mm"
										showTimeSelect
										dateFormat="dd.MM.yyyy - HH:mm"
										selected={timeEnd ? convertUTCToTimezoneDate(timeEnd as string) : null}
										onChange={(date: Date | null) => {
											setValue('timeEnd', date instanceof Date ? formatDateUTC(date) : null, {
												shouldDirty: true,
											});
										}}
									/>
								</InputGroup>
							</Flex>

							<Span fontSize={'12px'} color="rgb(82, 82, 91)">
								(UTC{DateTime.now().setZone(TIMEZONE).toFormat('ZZ')}) {TIMEZONE}
							</Span>
						</Flex>
					</Flex>
				</Drawer.Body>
				<Drawer.Footer>
					{discountCodeID ? (
						<>
							<RemoveDiscountCode discountCodeId={discountCodeID} isOpen={isRemoveOpen} setIsOpen={setIsRemoveOpen} />
							<Button id="removeDiscountCodeButton" marginRight={'auto'} onClick={() => setIsRemoveOpen(true)} variant="subtle">
								{t('general.remove_from_account')}
							</Button>
						</>
					) : null}
					<Button onClick={() => closeDrawer('discountCodeDrawer')} variant="outline">
						{t('general.cancel')}
					</Button>
					<Button type="submit" disabled={isUpdatingDiscountCode || isAddingDiscountCode || !isDirty}>
						{isUpdatingDiscountCode ? t('general.please_wait') : t('general.save')}
					</Button>
				</Drawer.Footer>

				<Drawer.CloseTrigger asChild>
					<CloseButton size="sm" />
				</Drawer.CloseTrigger>
			</form>
		</FormProvider>
	);
}

function RemoveDiscountCode({ discountCodeId, isOpen, setIsOpen }: { discountCodeId: number; isOpen: boolean; setIsOpen: (value: boolean) => void }) {
	const { t } = useTranslation();
	const { closeDrawer } = useDrawer();
	const { mutate, isPending: isDeletingDiscountCode } = useDeleteDiscountCode();
	const queryClient = useQueryClient();

	function removeDiscountCode(): void {
		mutate(discountCodeId, {
			onSuccess: async () => {
				toaster.create({
					title: t('general.removed'),
					type: 'success',
				});
				queryClient.refetchQueries({ queryKey: ['discountCodes'] });
				closeDrawer('discountCodeDrawer');
			},
		});
	}

	return (
		<Dialog.Root open={isOpen} onOpenChange={e => setIsOpen(e.open)} placement="center">
			<Dialog.Backdrop />
			<Portal>
				<Dialog.Positioner pointerEvents="none">
					<DialogContent>
						<Dialog.Header>
							<Dialog.Title>{t('general.remove_discount_code')}</Dialog.Title>
						</Dialog.Header>
						<Dialog.CloseTrigger />
						<DialogBody>
							<p>{t('general.remove_discount_code')}?</p>
						</DialogBody>
						<DialogFooter>
							<Dialog.ActionTrigger asChild>
								<Button variant="outline">{t('general.cancel')}</Button>
							</Dialog.ActionTrigger>
							<Button id="confirmRemoveDiscountCodeButton" disabled={isDeletingDiscountCode} variant="subtle" onClick={removeDiscountCode}>
								{isDeletingDiscountCode ? t('general.please_wait') : t('general.remove')}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog.Positioner>
			</Portal>
		</Dialog.Root>
	);
}
